import { Controller } from "@hotwired/stimulus"

/**
 * Image Compare Controller
 *
 * Manages 4 comparison modes for observability media:
 * - side_by_side: grid layout with synchronized zoom/pan per viewport
 * - slider: draggable vertical divider with clip-path
 * - overlay: transparency control via opacity slider
 * - diff: pixel-level difference heatmap via Canvas API
 *
 * Zoom: scroll wheel (all modes)
 * Pan: Space + drag (slider/overlay), plain drag (side-by-side/diff)
 * Reset: double-click
 */
export default class extends Controller {
  static targets = [
    "imageA",
    "imageB",
    "viewport",
    "zoomable",
    "slider",
    "canvas",
    "opacitySlider",
    "opacityLabel",
    "zoomLevel",
    "loading",
  ]
  static values = {
    mode: { type: String, default: "side_by_side" },
  }

  declare imageATarget: HTMLImageElement
  declare imageBTarget: HTMLImageElement
  declare viewportTargets: HTMLElement[]
  declare zoomableTargets: HTMLElement[]
  declare hasSliderTarget: boolean
  declare sliderTarget: HTMLElement
  declare hasCanvasTarget: boolean
  declare canvasTarget: HTMLCanvasElement
  declare hasOpacitySliderTarget: boolean
  declare opacitySliderTarget: HTMLInputElement
  declare hasOpacityLabelTarget: boolean
  declare opacityLabelTarget: HTMLElement
  declare hasZoomLevelTarget: boolean
  declare zoomLevelTarget: HTMLElement
  declare hasLoadingTarget: boolean
  declare loadingTarget: HTMLElement
  declare modeValue: string

  private sliderPosition = 50
  private isDragging = false // slider handle drag
  private zoom = 1
  private panX = 0
  private panY = 0
  private isPanning = false
  private panStartX = 0
  private panStartY = 0
  private spaceDown = false // spacebar held for pan mode
  private diffCache: ImageData | null = null

  private boundPointerUp = this.handlePointerUp.bind(this)
  private boundPointerMove = this.handlePointerMove.bind(this)
  private boundKeyDown = this.handleKeyDown.bind(this)
  private boundKeyUp = this.handleKeyUp.bind(this)

  connect() {
    document.addEventListener("pointerup", this.boundPointerUp)
    document.addEventListener("pointermove", this.boundPointerMove)
    document.addEventListener("keydown", this.boundKeyDown)
    document.addEventListener("keyup", this.boundKeyUp)

    if (this.modeValue === "diff") {
      this.waitForImagesAndComputeDiff()
    }
  }

  disconnect() {
    document.removeEventListener("pointerup", this.boundPointerUp)
    document.removeEventListener("pointermove", this.boundPointerMove)
    document.removeEventListener("keydown", this.boundKeyDown)
    document.removeEventListener("keyup", this.boundKeyUp)
  }

  // ── Keyboard state (Space for pan) ──────────────────────

  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === " ") {
      // Prevent page scroll when Space is used for pan
      event.preventDefault()
      if (!this.spaceDown) {
        this.spaceDown = true
        this.element.classList.add("cursor-grab")
      }
    }
  }

  private handleKeyUp(event: KeyboardEvent) {
    if (event.key === " ") {
      this.spaceDown = false
      this.isPanning = false
      this.element.classList.remove("cursor-grab", "cursor-grabbing")
    }
  }

  // ── Pointer events ──────────────────────────────────────

  private handlePointerMove(event: PointerEvent) {
    if (this.isDragging) {
      this.updateSlider(event)
    } else if (this.isPanning) {
      this.panX = event.clientX - this.panStartX
      this.panY = event.clientY - this.panStartY
      this.applyTransform()
      this.syncSliderClipPath()
    }
  }

  private handlePointerUp() {
    this.isDragging = false
    this.isPanning = false
    this.element.classList.remove("cursor-grabbing")
  }

  // ── Generic pointerdown handler for viewports ───────────

  viewportPointerDown(event: PointerEvent) {
    const mode = this.modeValue

    // Space+drag = pan in all modes (even at zoom=1 for repositioning)
    if (this.spaceDown) {
      this.startPanFromEvent(event)
      return
    }

    // Mode-specific default drag behavior
    if (mode === "slider") {
      // Plain drag = move slider (pan only via Space+drag)
      this.isDragging = true
      event.preventDefault()
      this.updateSlider(event)
    } else {
      // side_by_side, overlay, diff: plain drag = pan when zoomed
      if (this.zoom > 1) {
        this.startPanFromEvent(event)
      }
    }
  }

  private startPanFromEvent(event: PointerEvent) {
    this.isPanning = true
    this.panStartX = event.clientX - this.panX
    this.panStartY = event.clientY - this.panY
    this.element.classList.add("cursor-grabbing")
    event.preventDefault()
  }

  // ── Slider mode ─────────────────────────────────────────

  startSliderDrag(event: PointerEvent) {
    if (this.spaceDown) {
      this.startPanFromEvent(event)
      return
    }
    this.isDragging = true
    event.preventDefault()
    event.stopPropagation()
    ;(event.target as HTMLElement)?.setPointerCapture?.(event.pointerId)
    this.updateSlider(event)
  }

  private updateSlider(event: PointerEvent) {
    if (!this.hasSliderTarget) return
    const viewport = this.viewportTargets[0]
    if (!viewport) return

    // Slider position is always relative to the viewport (not the zoomed image)
    const rect = viewport.getBoundingClientRect()
    const x = event.clientX - rect.left
    this.sliderPosition = Math.max(0, Math.min(100, (x / rect.width) * 100))

    // Position the slider handle in viewport space
    this.sliderTarget.style.left = `${this.sliderPosition}%`

    // Compute clip-path in image space (accounting for zoom + pan)
    const zoomEl = this.zoomableTargets[0]
    if (zoomEl) {
      const zoomRect = zoomEl.getBoundingClientRect()
      const imageX = event.clientX - zoomRect.left
      const imagePercent = Math.max(0, Math.min(100, (imageX / zoomRect.width) * 100))
      this.imageBTarget.style.clipPath = `inset(0 0 0 ${imagePercent}%)`
    } else {
      this.imageBTarget.style.clipPath = `inset(0 0 0 ${this.sliderPosition}%)`
    }
  }

  // Recalculate clip-path from the slider handle's viewport position after zoom/pan changes
  private syncSliderClipPath() {
    if (!this.hasSliderTarget || this.modeValue !== "slider") return
    const zoomEl = this.zoomableTargets[0]
    if (!zoomEl) return

    // The slider handle is at sliderPosition% of the viewport.
    // Map that viewport-space X to the zoomable div's coordinate space.
    const viewport = this.viewportTargets[0]
    if (!viewport) return
    const vpRect = viewport.getBoundingClientRect()
    const handleX = vpRect.left + (this.sliderPosition / 100) * vpRect.width
    const zoomRect = zoomEl.getBoundingClientRect()
    const imagePercent = Math.max(0, Math.min(100, ((handleX - zoomRect.left) / zoomRect.width) * 100))
    this.imageBTarget.style.clipPath = `inset(0 0 0 ${imagePercent}%)`
  }

  sliderKeyboard(event: KeyboardEvent) {
    if (event.key === "ArrowLeft") {
      this.sliderPosition = Math.max(0, this.sliderPosition - 1)
    } else if (event.key === "ArrowRight") {
      this.sliderPosition = Math.min(100, this.sliderPosition + 1)
    } else {
      return
    }
    event.preventDefault()
    if (this.hasSliderTarget) {
      this.sliderTarget.style.left = `${this.sliderPosition}%`
      this.imageBTarget.style.clipPath = `inset(0 0 0 ${this.sliderPosition}%)`
    }
  }

  // ── Overlay mode ────────────────────────────────────────

  updateOpacity() {
    if (!this.hasOpacitySliderTarget) return
    const value = parseInt(this.opacitySliderTarget.value)
    this.imageBTarget.style.opacity = String(value / 100)
    if (this.hasOpacityLabelTarget) {
      this.opacityLabelTarget.textContent = `${value}%`
    }
  }

  // ── Diff mode ───────────────────────────────────────────

  private waitForImagesAndComputeDiff() {
    const imgA = this.imageATarget
    const imgB = this.imageBTarget
    const aLoaded = imgA.complete && imgA.naturalWidth > 0
    const bLoaded = imgB.complete && imgB.naturalWidth > 0

    if (aLoaded && bLoaded) { this.computeDiff(); return }

    let remaining = (aLoaded ? 0 : 1) + (bLoaded ? 0 : 1)
    const onLoad = () => { if (--remaining <= 0) this.computeDiff() }
    if (!aLoaded) imgA.addEventListener("load", onLoad, { once: true })
    if (!bLoaded) imgB.addEventListener("load", onLoad, { once: true })

    const onError = () => {
      if (this.hasLoadingTarget) this.loadingTarget.textContent = "Error loading images"
    }
    imgA.addEventListener("error", onError, { once: true })
    imgB.addEventListener("error", onError, { once: true })
  }

  computeDiff() {
    if (!this.hasCanvasTarget) return
    const isSvg = (img: HTMLImageElement) => (img.currentSrc || img.src).split("?")[0].toLowerCase().endsWith(".svg")
    if (isSvg(this.imageATarget) || isSvg(this.imageBTarget)) {
      if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
      return
    }
    if (this.diffCache) { this.renderDiff(this.diffCache); return }

    const imgA = this.imageATarget, imgB = this.imageBTarget
    if (!imgA.naturalWidth || !imgB.naturalWidth) return

    const w = Math.min(imgA.naturalWidth, imgB.naturalWidth, 4096)
    const h = Math.min(imgA.naturalHeight, imgB.naturalHeight, 4096)
    this.canvasTarget.width = w
    this.canvasTarget.height = h

    const offA = new OffscreenCanvas(w, h)
    const offB = new OffscreenCanvas(w, h)
    offA.getContext("2d")!.drawImage(imgA, 0, 0, w, h)
    offB.getContext("2d")!.drawImage(imgB, 0, 0, w, h)

    const dataA = offA.getContext("2d")!.getImageData(0, 0, w, h)
    const dataB = offB.getContext("2d")!.getImageData(0, 0, w, h)
    this.computeDiffChunked(dataA, dataB, new ImageData(w, h), w * h)
  }

  private computeDiffChunked(dataA: ImageData, dataB: ImageData, diff: ImageData, total: number, offset = 0) {
    const CHUNK = 100_000
    const end = Math.min(offset + CHUNK, total)
    for (let p = offset; p < end; p++) {
      const i = p * 4
      const mag = Math.min(255, (Math.abs(dataA.data[i] - dataB.data[i]) + Math.abs(dataA.data[i+1] - dataB.data[i+1]) + Math.abs(dataA.data[i+2] - dataB.data[i+2])) * 2)
      diff.data[i] = mag; diff.data[i+1] = 0; diff.data[i+2] = 0
      diff.data[i+3] = mag > 10 ? 255 : 50
    }
    if (end < total) {
      requestAnimationFrame(() => this.computeDiffChunked(dataA, dataB, diff, total, end))
    } else {
      this.diffCache = diff
      this.renderDiff(diff)
    }
  }

  private renderDiff(diff: ImageData) {
    this.canvasTarget.getContext("2d")!.putImageData(diff, 0, 0)
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
  }

  // ── Zoom & Pan ──────────────────────────────────────────

  zoomWheel(event: WheelEvent) {
    event.preventDefault()
    const delta = event.deltaY > 0 ? 0.9 : 1.1
    this.zoom = Math.max(1, Math.min(10, this.zoom * delta))
    if (this.zoom <= 1) { this.panX = 0; this.panY = 0 }
    this.applyTransform()
    this.syncSliderClipPath()
  }

  resetZoom() {
    this.zoom = 1; this.panX = 0; this.panY = 0
    this.applyTransform()
    this.syncSliderClipPath()
  }

  private applyTransform() {
    const transform = `scale(${this.zoom}) translate(${this.panX / this.zoom}px, ${this.panY / this.zoom}px)`

    // Transform all zoomable wrappers (one per column in side-by-side, one shared in other modes)
    for (const z of this.zoomableTargets) {
      z.style.transform = transform
      z.style.transformOrigin = "center center"
    }

    if (this.hasZoomLevelTarget) {
      this.zoomLevelTarget.textContent = this.zoom > 1 ? `${this.zoom.toFixed(1)}x` : ""
      this.zoomLevelTarget.classList.toggle("hidden", this.zoom <= 1)
    }
  }
}
